import { useStoreOwnStock } from '@gozoki/api'
import {
  type StoreContactService,
  type StoreProcessTaskType,
  storeProcessTaskTypeLabel,
  storeProcessTaskTypes,
} from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { Skeleton } from '@gozoki/ui/components/ui/skeleton'
import { cn } from '@gozoki/ui/lib/utils'
import { useState } from 'react'
import { ComingSoonModal } from './ComingSoonModal'
import { ContactModal } from './ContactModal'
import { ProcessModal } from './ProcessModal'
import { StockDetailModal } from './StockDetailModal'
import { CONTACT_DIRECTORY } from './contact-directory'
import {
  BookOpenIcon,
  CleanIcon,
  Contact02Icon,
  HeadsetIcon,
  MessageMultipleIcon,
  PackageDeliveredIcon,
  ShelvingUnitIcon,
  Truck24Icon,
} from './icons/dashboard-icons'
import { ChevronRightRoundIcon, LayerIcon } from './icons/LayerIcon'
import { CardDivider, CardTitle, DashboardCard, EmptyState } from './ui/DashboardCard'

/**
 * Figma 10715:57862 — STOCK RESTANT : total UVC de la boutique connectée puis une ligne par
 * catégorie produit, quantité en orange quand la catégorie passe sous son stock minimum du jour.
 *
 * Hauteur fixe : la liste défile à l'intérieur au-delà de ~6 catégories (le réseau en compte jusqu'à
 * 9, variable d'une boutique à l'autre) plutôt que de pousser CONTACT hors du canvas de 1453 px. On
 * a rogné 16 px sur cette carte (déjà défilable) pour les rendre à CONTACT, qui devait sinon scroller.
 */
const STOCK_RESTANT_HEIGHT = 327

export function StockRestantCard() {
  const { data, isLoading } = useStoreOwnStock()
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const openDetail = () => setIsDetailOpen(true)
  const categories = data?.categories ?? []

  return (
    <DashboardCard
      className="shrink-0 border-[#0000000F] bg-[#F0EDE2]"
      style={{ height: STOCK_RESTANT_HEIGHT }}
      onOpen={openDetail}
    >
      <CardTitle
        icon={<LayerIcon />}
        iconBoxClassName="bg-[#F9F8F1] text-[#4F6A47] size-[40px]"
        title="STOCK RESTANT"
        titleClassName="text-[#4F6A47]"
        onOpen={openDetail}
        openLabel="Ouvrir le détail du stock"
      />

      <CardDivider />

      <div className="flex h-[36px] items-center justify-end gap-[4px]">
        {isLoading ? (
          <Skeleton className="h-[36px] w-[120px]" />
        ) : (
          <>
            <span className="font-brand text-[32px] leading-[36px] font-extrabold text-[#354A31]">
              {data ? frenchFormat(data.totalStock) : '—'}
            </span>
            <span className="font-sans text-[14px] leading-[16px] font-semibold text-[#3E4F3F]">
              UVC
            </span>
          </>
        )}
      </div>

      {!isLoading && categories.length === 0 ? (
        <EmptyState>Aucun stock en boutique</EmptyState>
      ) : (
        // Déborde dans le padding droit de la carte : la barre de scroll s'y loge, hors des « u. ».
        <div className="-mr-[16px] flex min-h-0 flex-1 flex-col overflow-y-auto kiosk-scroll-y pr-[16px]">
          {categories.map((category) => (
            <div
              key={category.id}
              className="flex shrink-0 items-center gap-[10px] border-b border-[#0000000F] pt-[8px] pb-[9px]"
            >
              <span className="min-w-0 flex-1 truncate font-sans text-[12px] leading-[14px] font-medium text-[#354A31]">
                {category.label}
              </span>
              <span
                className={cn(
                  'shrink-0 text-right font-sans text-[16px] leading-[18px] font-semibold',
                  category.belowMinimum ? 'text-[#EFA031]' : 'text-[#354A31]',
                )}
              >
                {frenchFormat(category.stock)} u.
              </span>
            </div>
          ))}
        </div>
      )}

      {isDetailOpen ? <StockDetailModal onClose={() => setIsDetailOpen(false)} /> : null}
    </DashboardCard>
  )
}

/**
 * Figma 10715:57901 — SUIVI SAV.
 *
 * ⚠️ NON BRANCHÉ : il n'existe aujourd'hui aucun modèle de ticket SAV en base (pas de table, pas
 * d'endpoint), donc rien à afficher. En attendant, la carte annonce « Disponible bientôt » et un tap
 * dessus ouvre une modale « bientôt disponible » plutôt que d'afficher des tickets factices.
 * Quand la source existera : brancher le hook de récupération ici, retirer la `ComingSoonModal` et
 * rétablir la liste de la maquette (n° de ticket + date à droite, statut en dessous, dernier item
 * estompé).
 */
export function SuiviSavCard() {
  const [isSoonOpen, setIsSoonOpen] = useState(false)
  const openSoon = () => setIsSoonOpen(true)

  return (
    <DashboardCard className="h-[254px] shrink-0 border-[#E9EBE9] bg-[#F3F6F3]" onOpen={openSoon}>
      <CardTitle
        icon={<HeadsetIcon />}
        iconBoxClassName="bg-[#E5EBE5] text-[#4F6A47] size-[40px]"
        title="SUIVI SAV"
        titleClassName="text-[#4F6A47]"
        openLabel="Ouvrir le suivi SAV"
        onOpen={openSoon}
      />

      <EmptyState>Disponible bientôt</EmptyState>

      {isSoonOpen ? (
        <ComingSoonModal title="Suivi SAV" onClose={() => setIsSoonOpen(false)} />
      ) : null}
    </DashboardCard>
  )
}

/**
 * Figma 10715:57925 — PROCESS.
 *
 * Lanceur de tutos : une ligne fixe par type de process (indépendant du planning), pour que chaque
 * mode opératoire (SOP) reste accessible même quand le process n'est pas planifié cette semaine. Une
 * pastille d'icône par type + libellé + chevron ; le clic ouvre la `ProcessModal` sur l'onglet du
 * type (les types sans SOP — INVENTORY, CLIENT_RELATION — retombent sur l'onglet placeholder).
 * Icônes dans `icons/dashboard-icons.tsx`.
 */
const PROCESS_TASK_ICON: Record<StoreProcessTaskType, typeof CleanIcon> = {
  CLEANING: CleanIcon,
  RECEPTION: PackageDeliveredIcon,
  LIVRAISON: Truck24Icon,
  INVENTORY: ShelvingUnitIcon,
  CLIENT_RELATION: MessageMultipleIcon,
}

export function ProcessCard() {
  const [selectedType, setSelectedType] = useState<StoreProcessTaskType | null>(null)

  return (
    <DashboardCard className="min-h-0 flex-1 border-[#E9EBE9] bg-[#F3F6F3]">
      <CardTitle
        icon={<BookOpenIcon />}
        iconBoxClassName="bg-[#E5EBE5] text-[#4F6A47] size-[40px]"
        title="PROCESS"
        titleClassName="text-[#4F6A47]"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-[8px] overflow-y-auto kiosk-scroll-y">
        {storeProcessTaskTypes.map((type) => {
          const Icon = PROCESS_TASK_ICON[type]
          // Maquette 10715:57925 : une ligne = pastille d'icône + libellé + chevron d'ouverture.
          return (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              aria-label={`Ouvrir le tuto ${storeProcessTaskTypeLabel[type]}`}
              className="flex w-full shrink-0 cursor-pointer items-center gap-[12px] rounded-[12px] border border-[#E5EBE5] bg-white p-[12px] text-left"
            >
              <span className="flex size-[28px] shrink-0 items-center justify-center rounded-[6px] bg-[#E5EBE5] text-[#4F6A47]">
                <Icon className="size-[18px]" />
              </span>
              <span className="min-w-0 flex-1 truncate font-brand text-[16px] leading-[18px] font-semibold text-[#4F6A47]">
                {storeProcessTaskTypeLabel[type]}
              </span>
              <ChevronRightRoundIcon className="shrink-0 text-[#4F6A47]" />
            </button>
          )
        })}
      </div>

      {selectedType ? (
        <ProcessModal type={selectedType} onClose={() => setSelectedType(null)} />
      ) : null}
    </DashboardCard>
  )
}

/**
 * Figma 10715:58022 — CONTACT.
 *
 * Annuaire d'astreinte : 4 CTA (Supply / S.A.V / I.T / Urgence). Le clic ouvre une modale de
 * confirmation d'appel (`ContactModal`, Figma 10263:2300) qui lance un lien `tel:` et journalise
 * l'appel dans l'audit (`store.contact_called`).
 *
 * ⚠️ Contacts et numéros = PLACEHOLDERS (cf. `contact-directory.ts`). TODO: vrais numéros à fournir
 * par le client, éventuellement par boutique (à ce stade la liste est la même pour toutes).
 */
export function ContactCard({ storeLabel }: { storeLabel?: string }) {
  const [openService, setOpenService] = useState<StoreContactService | null>(null)

  return (
    <DashboardCard className="h-[306px] shrink-0 border-[#E9EBE9] bg-[#F3F6F3]">
      <CardTitle
        icon={<Contact02Icon />}
        iconBoxClassName="bg-[#E5EBE5] text-[#4F6A47] size-[40px] rounded-[8px]"
        title="CONTACT"
        titleClassName="text-[#4F6A47]"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-[8px] overflow-y-auto kiosk-scroll-y">
        {CONTACT_DIRECTORY.map((service) => (
          <button
            key={service.key}
            type="button"
            onClick={() => setOpenService(service.key)}
            aria-label={`Contacter ${service.label}`}
            className="flex w-full shrink-0 cursor-pointer items-center gap-[12px] rounded-[12px] border p-[8px]"
            style={{
              backgroundColor: service.theme.ctaBg,
              borderColor: service.theme.ctaBorder,
              color: service.theme.ctaText,
            }}
          >
            <span
              className="flex shrink-0 items-center justify-center rounded-[6px] p-[4px]"
              style={{ backgroundColor: service.key === 'urgence' ? '#FBC6C6' : '#E5EBE5' }}
            >
              <service.Icon className="size-[20px]" />
            </span>
            <span className="min-w-0 flex-1 text-left font-sans text-[16px] leading-[18px] font-semibold">
              {service.label}
            </span>
            <ChevronRightRoundIcon />
          </button>
        ))}
      </div>

      {openService ? (
        <ContactModal
          service={openService}
          storeLabel={storeLabel}
          onClose={() => setOpenService(null)}
        />
      ) : null}
    </DashboardCard>
  )
}
